import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import {
  EditOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import apiService, { Lead } from '../../services/api';
import { getLeadCategoryStyle } from '../../utils/leadCategoryColors';
import { workspaceSurface, workspaceCardHead, workspaceCardBody } from './workspaceTheme';

const { Text } = Typography;

export const LeadCardTitle: React.FC<{ lead: Lead }> = ({ lead }) => {
  const area = Array.isArray(lead.business_area)
    ? (lead.business_area as unknown as string[]).join(',')
    : lead.business_area || '';
  const isTelecom = area.includes('telecommunications');
  return (
    <Space>
      {isTelecom ? (
        <WifiOutlined style={{ color: '#1890ff' }} />
      ) : (
        <ThunderboltOutlined style={{ color: '#faad14' }} />
      )}
      <span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span>
      {lead.section && <Tag color="blue">{lead.section}</Tag>}
      {lead.lead_status && <Tag>{lead.lead_status}</Tag>}
    </Space>
  );
};

export interface LeadHeaderHandle {
  openEdit: () => void;
}

interface LeadHeaderProps {
  lead: Lead;
  onLeadChange: (lead: Lead) => void;
  /** When true, renders only details + modal (no card chrome). Use with parent shell + ref for Editar. */
  embedded?: boolean;
}

const LeadHeader = forwardRef<LeadHeaderHandle, LeadHeaderProps>(function LeadHeader(
  { lead, onLeadChange, embedded = false },
  ref
) {
  const [editVisible, setEditVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const area = Array.isArray(lead.business_area)
    ? (lead.business_area as unknown as string[]).join(',')
    : lead.business_area || '';
  const isTelecom = area.includes('telecommunications');

  const openEdit = useCallback(() => {
    form.setFieldsValue({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      address: lead.address,
      city: lead.city,
      postal_code: lead.postal_code,
      current_provider: lead.current_provider,
      dif: lead.dif,
    });
    setEditVisible(true);
  }, [lead, form]);

  useImperativeHandle(ref, () => ({ openEdit }), [openEdit]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const updated = await apiService.updateLead(lead.id, values);
      message.success('Cliente atualizado.');
      onLeadChange(updated as Lead);
      setEditVisible(false);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error('Erro ao atualizar o cliente.');
    } finally {
      setSaving(false);
    }
  };

  const editModal = (
    <Modal
      open={editVisible}
      title="Editar Cliente"
      onCancel={() => setEditVisible(false)}
      onOk={handleSave}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={saving}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ type: 'email', message: 'Email inválido' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Telefone">
          <Input />
        </Form.Item>
        <Form.Item name="dif" label="NIF">
          <Input />
        </Form.Item>
        {isTelecom && (
          <Form.Item name="current_provider" label="Operador Atual">
            <Input />
          </Form.Item>
        )}
        <Form.Item name="address" label="Morada">
          <Input />
        </Form.Item>
        <Form.Item name="city" label="Cidade">
          <Input />
        </Form.Item>
        <Form.Item name="postal_code" label="Código Postal">
          <Input placeholder="0000-000" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const details = (
    <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} colon={false}>
      <Descriptions.Item label={<><MailOutlined /> Email</>}>
        {lead.email || <Text type="secondary">—</Text>}
      </Descriptions.Item>
      <Descriptions.Item label={<><PhoneOutlined /> Telefone</>}>
        {lead.phone || <Text type="secondary">—</Text>}
      </Descriptions.Item>
      <Descriptions.Item label={<><IdcardOutlined /> NIF</>}>
        {lead.dif || <Text type="secondary">—</Text>}
      </Descriptions.Item>
      <Descriptions.Item label={<><HomeOutlined /> Morada</>} span={2}>
        {[lead.address, lead.city, lead.postal_code].filter(Boolean).join(', ') || (
          <Text type="secondary">—</Text>
        )}
      </Descriptions.Item>
      {isTelecom && (
        <Descriptions.Item label="Operador Atual">
          {lead.current_provider || <Text type="secondary">—</Text>}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Tipo">
        {lead.customer_type === 'business' ? 'Empresarial' : 'Residencial'}
      </Descriptions.Item>
    </Descriptions>
  );

  if (embedded) {
    return (
      <>
        {editModal}
        <div style={{ padding: '16px 20px' }}>{details}</div>
      </>
    );
  }

  return (
    <Card
      style={{ ...workspaceSurface, marginBottom: 16 }}
      headStyle={workspaceCardHead}
      bodyStyle={{ ...workspaceCardBody, padding: 16 }}
      title={<LeadCardTitle lead={lead} />}
      extra={
        <Button icon={<EditOutlined />} onClick={openEdit} size="small">
          Editar
        </Button>
      }
    >
      {editModal}
      {details}
    </Card>
  );
});

export default LeadHeader;
